import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';

const DEFAULT_ALBUMS = [
  {
    id: 'album-1',
    title: 'Workforce Welfare & Healthcare Camps',
    description: 'Free health checkups, ESIC card distributions, and welfare orientation sessions conducted across railway goods sheds.',
    category: 'Welfare',
    coverImage: '/SG (4).jpg',
    date: '2026',
    photos: [
      { id: 'p1', url: '/SG (4).jpg', caption: 'ESIC Card Distribution Camp', type: 'image' },
      { id: 'p2', url: '/SG (2).jpg', caption: 'Worker Registration & Verification Drive', type: 'image' },
      { id: 'p3', url: '/SG (8).jpg', caption: 'Welfare Guidance & Support Hub', type: 'image' }
    ]
  },
  {
    id: 'album-2',
    title: 'NFR Zone Goodshed Freight Operations',
    description: 'Active freight cargo handling, loading/unloading logistics, and digital yard monitoring across Northeast Frontier Railway terminals.',
    category: 'Operations',
    coverImage: '/SG (1).jpg',
    date: '2026',
    photos: [
      { id: 'p4', url: '/SG (1).jpg', caption: 'Goodshed Heavy Freight Handling', type: 'image' },
      { id: 'p5', url: '/SG (6).jpg', caption: 'Cargo Logistics Management', type: 'image' },
      { id: 'p6', url: '/g1.png', caption: 'Goods Shed Cargo Supervision', type: 'image' }
    ]
  },
  {
    id: 'album-3',
    title: 'Safety Inspection & Protocol Drives',
    description: 'Routine safety audits, PPE enforcement, and high-visibility gear distribution for railway goodshed personnel.',
    category: 'Safety',
    coverImage: '/SG (3).jpg',
    date: '2026',
    photos: [
      { id: 'p7', url: '/SG (3).jpg', caption: 'Freight Yard Safety Audit', type: 'image' },
      { id: 'p8', url: '/SG (10).jpg', caption: 'Safety Gear & Helmet Verification', type: 'image' }
    ]
  },
  {
    id: 'album-4',
    title: 'Zonal Corporate & Administrative Events',
    description: 'Board meetings, Ministry of Corporate Affairs compliance briefings, and regional partner alignment meets.',
    category: 'Events',
    coverImage: '/SG (5).jpg',
    date: '2026',
    photos: [
      { id: 'p9', url: '/SG (5).jpg', caption: 'NFR Zonal Office Briefing', type: 'image' },
      { id: 'p10', url: '/SG (9).jpg', caption: 'Operational Strategy & Planning Meeting', type: 'image' },
      { id: 'p11', url: '/g3.png', caption: 'SGCL Zonal Infrastructure Expansion', type: 'image' }
    ]
  }
];

const checkIsVideo = (item) => {
  if (!item) return false;
  if (item.type === 'video') return true;
  const url = item.url ? item.url.toLowerCase() : '';
  return url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi') || url.includes('youtube.com') || url.includes('youtu.be');
};

const formatImageUrl = (url) => {
  if (!url) return '/SG (1).jpg';
  if (url.startsWith('/uploads')) return `${API_BASE_URL}${url}`;
  return url;
};

const Gallery = () => {
  const [albums, setAlbums] = useState(DEFAULT_ALBUMS);
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const fetchAlbums = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/albums`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setAlbums(data);
        }
      }
    } catch (err) {
      console.log('Using default albums fallback:', err);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  const categories = ['All', ...new Set(albums.map(a => a.category).filter(Boolean))];

  const filteredAlbums = activeCategory === 'All'
    ? albums
    : albums.filter(a => a.category === activeCategory);

  const currentPhotos = selectedAlbum ? (selectedAlbum.photos || []) : [];
  const currentMedia = (selectedAlbum && lightboxIndex !== null && currentPhotos[lightboxIndex]) ? currentPhotos[lightboxIndex] : null;

  const handlePrevMedia = useCallback(() => {
    if (!selectedAlbum || !selectedAlbum.photos || selectedAlbum.photos.length === 0) return;
    const total = selectedAlbum.photos.length;
    setLightboxIndex((prev) => (prev === null || prev === 0 ? total - 1 : prev - 1));
  }, [selectedAlbum]);

  const handleNextMedia = useCallback(() => {
    if (!selectedAlbum || !selectedAlbum.photos || selectedAlbum.photos.length === 0) return;
    const total = selectedAlbum.photos.length;
    setLightboxIndex((prev) => (prev === null || prev === total - 1 ? 0 : prev + 1));
  }, [selectedAlbum]);

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'ArrowLeft') handlePrevMedia();
      if (e.key === 'ArrowRight') handleNextMedia();
      if (e.key === 'Escape') setLightboxIndex(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, handlePrevMedia, handleNextMedia]);

  return (
    <div className="bg-slate-50 min-h-screen font-sans pb-20">
      {/* Hero Banner */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '360px' }}>
        <img src="/SG (5).jpg" alt="SGCL Gallery" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#0d2d6b]/90 to-[#0d2d6b]/60" />
        <div className="absolute top-0 right-0 opacity-5 text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>

        <div className="relative z-10 px-4 md:px-20 py-16 max-w-4xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">
            Media &amp; Archives
          </span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-5 leading-tight uppercase italic">
            Photo &amp; Video <span className="text-govt-orange">Albums</span>
          </h1>
          <p className="text-blue-100 font-bold border-l-4 border-govt-orange pl-5 italic max-w-2xl leading-relaxed text-base">
            Explore photos and videos of Goodshed Operations, Worker Enrolment Drives, Health Camps, and Corporate Events across the Northeast Frontier Region.
          </p>
        </div>

        {/* Tricolor border strip */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* Category Filter Pills */}
      <div className="container mx-auto px-4 md:px-20 mt-10">
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest border transition-all duration-200 ${
                activeCategory === cat
                  ? 'bg-govt-blue text-white border-govt-blue shadow-lg scale-105'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-govt-blue hover:text-govt-blue'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Albums Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAlbums.map((album) => {
            const mediaCount = (album.photos || []).length;
            const coverUrl = formatImageUrl(album.coverImage);

            return (
              <div
                key={album.id}
                onClick={() => setSelectedAlbum(album)}
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border border-slate-100 flex flex-col justify-between"
              >
                <div>
                  {/* Cover Container */}
                  <div className="relative h-60 overflow-hidden bg-slate-900">
                    <img
                      src={coverUrl}
                      alt={album.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90" />
                    
                    <span className="absolute top-4 right-4 bg-govt-orange text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
                      {album.category}
                    </span>

                    <span className="absolute bottom-4 left-4 bg-white/20 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-widest px-3.5 py-1.5 rounded-xl border border-white/30 flex items-center gap-1.5">
                      <span>🎬</span> {mediaCount} {mediaCount === 1 ? 'Media Item' : 'Media Items'}
                    </span>
                  </div>

                  {/* Info Block */}
                  <div className="p-6">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">
                      Album &bull; {album.date || '2026'}
                    </span>
                    <h3 className="font-black text-slate-800 text-lg uppercase tracking-tight group-hover:text-govt-orange transition-colors leading-snug mb-2">
                      {album.title}
                    </h3>
                    <p className="text-slate-500 text-xs font-semibold leading-relaxed line-clamp-2">
                      {album.description || 'View photos and videos inside this SGCL official album.'}
                    </p>
                  </div>
                </div>

                {/* Footer Action */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center text-xs font-black uppercase tracking-widest text-govt-blue group-hover:text-govt-orange transition-colors">
                  <span>Open Album Gallery</span>
                  <span className="text-base group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Album Media Grid Modal */}
      {selectedAlbum && (
        <div
          className="fixed inset-0 bg-slate-950/90 z-[100] flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto"
          onClick={() => setSelectedAlbum(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-5xl w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 my-8 border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-govt-blue text-white p-6 md:p-8 flex justify-between items-start border-b-4 border-govt-orange">
              <div>
                <span className="bg-govt-orange text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-2 inline-block">
                  {selectedAlbum.category}
                </span>
                <h2 className="text-2xl md:text-3xl font-black uppercase italic tracking-tight">{selectedAlbum.title}</h2>
                <p className="text-blue-200 text-xs font-semibold mt-1 max-w-2xl">{selectedAlbum.description}</p>
              </div>
              <button
                onClick={() => setSelectedAlbum(null)}
                className="bg-white/10 hover:bg-white text-white hover:text-black w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl transition-all shrink-0 ml-4"
              >
                ✕
              </button>
            </div>

            {/* Media Items Grid inside Album */}
            <div className="p-6 md:p-8 max-h-[65vh] overflow-y-auto bg-slate-50">
              {(!selectedAlbum.photos || selectedAlbum.photos.length === 0) ? (
                <div className="text-center py-16 text-slate-400 font-bold italic">
                  No photos or videos available in this album yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                  {selectedAlbum.photos.map((item, idx) => {
                    const mediaUrl = formatImageUrl(item.url);
                    const isVid = checkIsVideo(item);

                    return (
                      <div
                        key={idx}
                        onClick={() => setLightboxIndex(idx)}
                        className="group relative bg-slate-950 rounded-2xl overflow-hidden shadow-md cursor-pointer border border-slate-200 h-56 flex items-center justify-center"
                      >
                        {isVid ? (
                          <>
                            <video src={mediaUrl} className="w-full h-full object-cover opacity-80 group-hover:opacity-100" muted />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-all">
                              <div className="w-14 h-14 rounded-full bg-govt-orange/90 text-white flex items-center justify-center text-2xl shadow-xl group-hover:scale-110 transition-transform">
                                ▶
                              </div>
                            </div>
                            <span className="absolute top-3 left-3 bg-red-600 text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full shadow">
                              🎬 Video
                            </span>
                          </>
                        ) : (
                          <img
                            src={mediaUrl}
                            alt={item.caption || selectedAlbum.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-40 transition-opacity" />
                        <div className="absolute bottom-0 inset-x-0 p-3 text-white">
                          <p className="text-xs font-bold truncate leading-tight">{item.caption || selectedAlbum.title}</p>
                          <p className="text-[9px] text-govt-orange font-black uppercase tracking-widest mt-0.5">Click to view 🔍</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 bg-white border-t border-slate-100 flex justify-between items-center">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                Showing {selectedAlbum.photos ? selectedAlbum.photos.length : 0} items
              </span>
              <button
                onClick={() => setSelectedAlbum(null)}
                className="bg-govt-blue text-white px-8 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-govt-orange transition-colors"
              >
                Close Album
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal with Prev/Next Navigation Arrows & Video Support */}
      {currentMedia && (
        <div
          className="fixed inset-0 bg-black/95 z-[120] flex items-center justify-center p-4 backdrop-blur-lg animate-in fade-in duration-200 select-none"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Main Lightbox Container */}
          <div
            className="relative max-w-6xl w-full bg-slate-950 rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute top-4 right-4 z-30 bg-white/20 hover:bg-white text-white hover:text-black w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl transition-all shadow-xl"
              title="Close (Esc)"
            >
              ✕
            </button>

            {/* Left Navigation Arrow */}
            <button
              onClick={handlePrevMedia}
              className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/65 text-white/80 hover:text-white backdrop-blur-md w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-2xl font-black transition-all shadow-2xl hover:scale-110 active:scale-95 border border-white/20"
              title="Previous (Left Arrow)"
            >
              ❮
            </button>

            {/* Right Navigation Arrow */}
            <button
              onClick={handleNextMedia}
              className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/65 text-white/80 hover:text-white backdrop-blur-md w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-2xl font-black transition-all shadow-2xl hover:scale-110 active:scale-95 border border-white/20"
              title="Next (Right Arrow)"
            >
              ❯
            </button>

            {/* Media Player / Image Viewer Container */}
            <div className="relative h-[72vh] flex items-center justify-center p-4 bg-slate-950">
              {checkIsVideo(currentMedia) ? (
                <video
                  src={formatImageUrl(currentMedia.url)}
                  controls
                  autoPlay
                  className="max-h-[68vh] max-w-full rounded-2xl shadow-2xl border border-white/10"
                />
              ) : (
                <img
                  src={formatImageUrl(currentMedia.url)}
                  alt={currentMedia.caption || selectedAlbum.title}
                  className="max-h-[68vh] max-w-full object-contain rounded-2xl shadow-2xl"
                />
              )}
            </div>

            {/* Footer Bar with Caption and Counter */}
            <div className="p-5 bg-slate-900 text-white flex flex-col sm:flex-row justify-between items-center gap-3 border-t-4 border-govt-orange">
              <div className="text-center sm:text-left">
                <span className="text-[10px] font-black uppercase text-govt-orange tracking-widest">
                  {checkIsVideo(currentMedia) ? '🎬 Video Item' : '📷 Photo Item'} &bull; {selectedAlbum.title}
                </span>
                <h4 className="text-base font-black uppercase tracking-wide leading-tight mt-0.5">
                  {currentMedia.caption || selectedAlbum.title}
                </h4>
              </div>

              {/* Counter Pill & Keyboard Hint */}
              <div className="flex items-center gap-3">
                <span className="bg-white/10 px-4 py-1.5 rounded-full text-xs font-mono font-bold text-blue-200 border border-white/10">
                  {lightboxIndex + 1} / {currentPhotos.length}
                </span>
                <span className="hidden md:inline-block text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                  Use ◀ ▶ keys to navigate
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
